import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../../components/ui/accordion'
import { useAuth } from '../../context/AuthContext'
import { companyAPI } from '../../services/api'
import { toast } from '../../hooks/use-toast'
import CompanyDetailsModal from '../../components/company/CompanyDetailsModal'
import NetworkingPassesModal from '../../components/company/NetworkingPassesModal'
import PresentationModal from '../../components/company/PresentationModal'
import DemoGuidelinesModal from '../../components/company/DemoGuidelinesModal'
import PitchDeckFeedbackModal from '../../components/company/PitchDeckFeedbackModal'
import PitchDeckSubmissionModal from '../../components/company/PitchDeckSubmissionModal'
import BrandPromotionModal from '../../components/company/BrandPromotionModal'
import TechnicalSetupModal from '../../components/company/TechnicalSetupModal'
import PromotionalFlyerModal from '../../components/company/PromotionalFlyerModal'
import CEOFeedbackModal from '../../components/company/CEOFeedbackModal'
import {
  CheckCircle,
  Clock,
  ChevronRight,
  Lock,
  Trophy,
  Award,
  Calendar,
  Building2
} from 'lucide-react'

export default function Timeline() {
  const { user, hasLimitedAccess } = useAuth()
  const navigate = useNavigate()
  const [activeModal, setActiveModal] = useState(null)
  const [guidelinesRead, setGuidelinesRead] = useState(false)
  const [networkingPassesCompleted, setNetworkingPassesCompleted] = useState(false)
  const [networkingPassesLocked, setNetworkingPassesLocked] = useState(true)
  const [pitchDeckSubmitted, setPitchDeckSubmitted] = useState(false)
  const [presenterInfoSubmitted, setPresenterInfoSubmitted] = useState(false)
  const [technicalChecklistCompleted, setTechnicalChecklistCompleted] = useState(false)
  const [promotionalFlyerEnabled, setPromotionalFlyerEnabled] = useState(false)
  const [brandPromotionCompleted, setBrandPromotionCompleted] = useState(false)
  const [demoTimeSlot, setDemoTimeSlot] = useState(null)
  const isIntakeCompleted = user?.companyId && user?.intakeCompleted

  // Check statuses on component mount - only for full access users
  useEffect(() => {
    fetchCompanyInfo() // Fetch company info for time slots
    if (!hasLimitedAccess) {
      checkGuidelinesStatus()
      checkPitchDeckStatus()
      checkTechnicalChecklistStatus()
    }
    // Always check networking passes, promotional flyer and brand promotion as they're available for all roles
    checkNetworkingPassesStatus()
    checkPromotionalFlyerStatus()
    checkBrandPromotionStatus()
  }, [hasLimitedAccess])

  const fetchCompanyInfo = async () => {
    if (user?.companyId) {
      try {
        const response = await companyAPI.getCompanyInfo()
        if (response.data.success && response.data.company) {
          setDemoTimeSlot(response.data.company.timeSlots || null)
        }
      } catch (error) {
        console.error('Error fetching company info:', error)
      }
    } else if (user?.companyTimeSlots) {
      // Fallback to user object if available
      setDemoTimeSlot(user.companyTimeSlots || null)
    }
  }

  const checkGuidelinesStatus = async () => {
    // Skip for limited access users
    if (hasLimitedAccess) return

    try {
      const response = await companyAPI.getGuidelinesStatus()
      if (response.data.success) {
        setGuidelinesRead(response.data.guidelinesRead || false)
      }
    } catch (error) {
      console.log('Could not fetch guidelines status')
    }
  }

  const checkNetworkingPassesStatus = async () => {
    try {
      const response = await companyAPI.getNetworkingPasses()
      console.log('Network passes response (Timeline):', response.data)
      if (response.data) {
        // Check if locked
        const isLocked = response.data.isLocked === true || response.data.networkingPassesLocked === true
        setNetworkingPassesLocked(isLocked)
        console.log('Network passes locked status (Timeline):', isLocked)

        // Check if passes have been distributed
        if (!isLocked && response.data.success) {
          setNetworkingPassesCompleted(response.data.distributionStatus === 'distributed')
        }
      }
    } catch (error) {
      console.log('Could not fetch networking passes status:', error)
      setNetworkingPassesLocked(true) // Keep locked on error
    }
  }

  const checkPitchDeckStatus = async () => {
    // Skip for limited access users
    if (hasLimitedAccess) return

    try {
      const response = await companyAPI.getPitchDeckFeedback()
      if (response.data.success && response.data.feedback) {
        // Check if presenter info is submitted (LinkedIn is required)
        if (response.data.feedback.presenterLinkedIn) {
          setPresenterInfoSubmitted(true)
        }
        // Check if pitch deck/video is submitted (at least one URL is required)
        if (response.data.feedback.documentUrl || response.data.feedback.videoUrl) {
          setPitchDeckSubmitted(true)
        }
      }
    } catch (error) {
      console.log('Could not fetch pitch deck feedback status')
    }
  }

  const checkTechnicalChecklistStatus = async () => {
    // Skip for limited access users
    if (hasLimitedAccess) return

    try {
      const response = await companyAPI.getTechnicalChecklist()
      if (response.data.success && response.data.checklist) {
        setTechnicalChecklistCompleted(response.data.isCompleted || false)
      }
    } catch (error) {
      console.log('Could not fetch technical checklist status')
    }
  }

  const checkPromotionalFlyerStatus = async () => {
    try {
      const response = await companyAPI.getPromotionalFlyerStatus()
      if (response.data && response.data.data) {
        setPromotionalFlyerEnabled(response.data.data.flyerEnabled || false)
      }
    } catch (error) {
      console.log('Could not fetch promotional flyer status')
    }
  }

  const checkBrandPromotionStatus = async () => {
    try {
      const response = await companyAPI.getBrandPromotion()
      if (response.data.success && response.data.data) {
        // Mark as completed if LinkedIn company profile exists (required field)
        const hasLinkedIn = response.data.data.linkedin?.organizationProfile
        setBrandPromotionCompleted(!!hasLinkedIn)
      }
    } catch (error) {
      console.log('Could not fetch brand promotion status')
    }
  }

  // Define all tasks
  const allTimelineTasks = [
    {
      id: 'company-details',
      title: 'Company Details & Logo',
      description: 'Complete your company profile to unlock all features',
      status: isIntakeCompleted ? 'completed' : 'pending',
      dueDate: 'Immediate',
      category: 'required',
      locked: false,
      allowedForLimited: true
    },
    {
      id: 'networking-passes',
      title: 'Get your IFTA passes',
      description: networkingPassesLocked ? 'Coming soon - Network pass allocation will be available shortly' : 'Distribute Gold, Silver Plus and Silver passes to your team',
      status: networkingPassesCompleted ? 'completed' : 'pending',
      dueDate: networkingPassesLocked ? 'Soon' : 'Nov 20',
      category: 'preparation',
      locked: !isIntakeCompleted || networkingPassesLocked,
      lockReason: networkingPassesLocked ? 'Coming Soon' : (!isIntakeCompleted ? 'Complete company details first' : null),
      allowedForLimited: true
    },
    {
      id: 'demo-guidelines',
      title: 'Demo Guidelines Review',
      description: 'Understand presentation requirements and format',
      status: guidelinesRead ? 'completed' : 'pending',
      dueDate: 'Oct 26',
      category: 'preparation',
      locked: false,
      allowedForLimited: false
    },
    // {
    //   id: 'ceo-feedback',
    //   title: 'Session on Demo Preparation with India FinTech Forum\'s CEO',
    //   description: 'Schedule and participate in feedback session with CEO',
    //   status: 'pending', // Always shows as pending - it's just an informational modal
    //   dueDate: 'Oct 17',
    //   category: 'preparation',
    //   locked: false,
    //   allowedForLimited: false
    // },
    {
      id: 'presenter-info',
      title: 'Presenter Information',
      description: 'Provide details about the person presenting at IFTA 2025',
      status: presenterInfoSubmitted ? 'completed' : 'pending',
      dueDate: 'Oct 26',
      category: 'preparation',
      locked: false,
      allowedForLimited: false
    },
    {
      id: 'brand-promotion',
      title: 'Brand Promotion',
      description: 'Provide social media profiles for promotional activities',
      status: brandPromotionCompleted ? 'completed' : 'pending',
      dueDate: 'Oct 26',
      category: 'preparation',
      locked: false,
    allowedForLimited: true
    },
    {
      id: 'promotional-flyer',
      title: 'Promotional Flyer Distribution',
      description: 'Distribute promotional flyers to IFTA attendees',
      status: promotionalFlyerEnabled ? 'opted-in' : 'opted-out',
      dueDate: 'Nov 10',
      category: 'marketing',
      locked: false,
      allowedForLimited: false, // Only for demo companies
      isOptional: true,
      checklist: [
        'Design and print up to 500 single-page colour flyers',
        'Ship flyers to the event management address',
        'Confirm receipt of flyers by the organizers'
      ],
      shippingAddress: {
        company: 'Drishtant',
        address: '406 Solus, Hiranandani Estate',
        city: 'Thane West - 400607',
        state: 'Maharashtra, India'
      }
    },
    {
      id: 'pitch-submission',
      title: 'Pitch Deck & Video Submission',
      description: 'Submit your presentation deck and demo video links',
      status: pitchDeckSubmitted ? 'completed' : 'pending',
      dueDate: 'Nov 10',
      category: 'preparation',
      locked: false,
      allowedForLimited: false
    },
    {
      id: 'device-prep',
      title: 'Technical Setup & Equipment Check',
      description: 'Prepare all equipment and backup materials for presentation',
      status: technicalChecklistCompleted ? 'completed' : 'pending',
      dueDate: 'Dec 5',
      category: 'technical',
      locked: false,
      allowedForLimited: false,
      checklist: [
        'Confirm laptop and power adapter compatibility',
        'Test HDMI/display adapters and cables',
        'Record presentation video (no live demos allowed)',
        'Create backup files on USB and cloud storage',
        'Test presentation setup and equipment'
      ],
      subtasks: {
        completed: 0,
        total: 5
      }
    },
    {
      id: 'event-day',
      title: 'Event Day Checklist',
      description: 'Final preparations and equipment verification',
      status: 'pending',
      dueDate: 'Dec 10',
      category: 'event',
      locked: false,
      allowedForLimited: false,
      checklist: [
        'Pack all equipment and adapters',
        'Charge all devices to 100%',
        'Arrive early for setup and testing',
        'Have technical support contacts ready'
      ]
    }
  ]

  // Filter tasks based on user role
  const timelineTasks = hasLimitedAccess
    ? allTimelineTasks.filter(task => task.allowedForLimited)
    : allTimelineTasks

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'opted-in':
        return 'bg-green-500/10 text-green-600 border-green-200'
      case 'in-progress':
        return 'bg-blue-500/10 text-blue-600 border-blue-200'
      case 'opted-out':
        return 'bg-gray-500/10 text-gray-600 border-gray-200'
      default:
        return 'bg-gray-500/10 text-gray-600 border-gray-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />
      case 'opted-in':
        return <CheckCircle className="h-4 w-4" />
      case 'in-progress':
        return <Clock className="h-4 w-4" />
      case 'opted-out':
        return <div className="h-4 w-4 rounded-full border-2 border-current" />
      default:
        return <div className="h-4 w-4 rounded-full border-2 border-current" />
    }
  }

  const handleTaskClick = (task) => {
    if (task.locked) {
      if (task.lockReason === 'Coming Soon') {
        toast.warning('Network pass allocation will be available soon. Please check back later.')
      } else if (task.lockReason) {
        toast.warning(task.lockReason)
      } else {
        toast.warning('Please complete the Company Details & Logo first to unlock this task.')
      }
      return
    }

    // Open appropriate modal based on task id
    if (task.id === 'company-details') {
      setActiveModal('company-details')
    } else if (task.id === 'networking-passes') {
      setActiveModal('networking-passes')
    } else if (task.id === 'presentation-upload') {
      setActiveModal('presentation')
    } else if (task.id === 'demo-guidelines') {
      setActiveModal('demo-guidelines')
    } else if (task.id === 'ceo-feedback') {
      setActiveModal('ceo-feedback')
    } else if (task.id === 'presenter-info') {
      setActiveModal('presenter-info')
    } else if (task.id === 'pitch-submission') {
      setActiveModal('pitch-submission')
    } else if (task.id === 'brand-promotion') {
      setActiveModal('brand-promotion')
    } else if (task.id === 'device-prep') {
      setActiveModal('technical-setup')
    } else if (task.id === 'promotional-flyer') {
      setActiveModal('promotional-flyer')
    }
    // Add more modal triggers as needed
  }

  return (
    <div className="container mx-auto px-4 py-4 sm:py-6">
      <div className="grid gap-3 sm:gap-4">
        {/* Demo Schedule Card - Only show when timeSlots is present */}
        {demoTimeSlot && (
          <Card className="overflow-hidden bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-500 text-white rounded-xl shadow-lg">
                    <Calendar className="h-5 w-5 sm:h-6 sm:w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">Your Demo Schedule</h3>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm sm:text-base">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4 text-purple-600" />
                        <span className="font-medium text-gray-700">December 10, 2025</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4 text-purple-600" />
                        <span className="font-medium text-gray-700">{demoTimeSlot}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-4 w-4 text-purple-600" />
                        <span className="text-gray-600">Aurika, Mumbai</span>
                      </div>
                    </div>
                  </div>
                </div>
                <Badge className="bg-purple-600 text-white hover:bg-purple-700 self-start sm:self-center">
                  Confirmed
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {timelineTasks.map((task) => {
          // Special rendering for Event Day Checklist with Award info
          if (task.id === 'event-day') {
            return (
              <Accordion key={task.id} type="single" collapsible className="w-full">
                <AccordionItem value="event-day" className="border-0">
                  <Card className="overflow-hidden">
                    <AccordionTrigger className="hover:no-underline p-0 [&[data-state=open]>div]:border-b [&>svg]:mr-4">
                      <div className="w-full">
                        <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 gap-3">
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-1.5 sm:p-2 rounded-full bg-amber-500/10 text-amber-600 border-amber-200">
                              <Award className="h-4 w-4" />
                            </div>
                            <div className="text-left">
                              <h3 className="font-medium text-sm sm:text-base">{task.title}</h3>
                              <p className="text-xs sm:text-sm text-muted-foreground">Information for December 10</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
                              December 10
                            </Badge>
                          </div>
                        </CardContent>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                        {/* Original Checklist */}
                        {task.checklist && (
                          <div>
                            <h4 className="font-semibold mb-3 text-sm sm:text-base">Event Day Preparation</h4>
                            <ul className="space-y-2">
                              {task.checklist.map((item, index) => (
                                <li key={index} className="flex items-start gap-2 text-xs sm:text-sm">
                                  <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Award Ceremony Section */}
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 border border-purple-200">
                          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                            <div className="p-3 sm:p-4 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-xl shadow-lg">
                              <Trophy className="h-6 w-6 sm:h-10 sm:w-10" />
                            </div>
                            <div>
                              <h3 className="text-lg sm:text-2xl font-bold text-gray-900">Award Ceremony</h3>
                              <p className="text-xs sm:text-base text-gray-600">
                                Winners announced on <span className="font-semibold">10th December 2025</span> <span className="hidden sm:inline">at Aurika, Mumbai</span>
                              </p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <h4 className="font-semibold text-gray-900">Winner Benefits</h4>
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="bg-white rounded-xl p-4 border shadow-sm">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="p-2 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-lg shadow-sm">
                                    <span className="text-2xl">🏆</span>
                                  </div>
                                  <h5 className="font-semibold text-gray-900">Fintech Startup of the Year</h5>
                                </div>
                                <p className="text-sm text-gray-600">
                                  Complimentary pass to attend <span className="font-semibold">Money 20/20 Asia 2026</span>
                                </p>
                              </div>

                              <div className="bg-white rounded-xl p-4 border shadow-sm">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="p-2 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-lg shadow-sm">
                                    <span className="text-2xl">🚀</span>
                                  </div>
                                  <h5 className="font-semibold text-gray-900">Fintech Scaleup of the Year</h5>
                                </div>
                                <p className="text-sm text-gray-600">
                                  Complimentary pass to attend <span className="font-semibold">Fintech Berlin 2026</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </Card>
                </AccordionItem>
              </Accordion>
            )
          }

          // Regular task rendering
          return (
            <Card
              key={task.id}
              className={`${task.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'} transition-all`}
              onClick={() => handleTaskClick(task)}
            >
              <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 gap-3">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                  <div className={`p-1.5 sm:p-2 rounded-full flex-shrink-0 ${
                    task.locked
                      ? 'bg-amber-500/10 text-amber-600 border-amber-200'
                      : getStatusColor(task.status)
                  }`}>
                    {task.locked ? <Lock className="h-3 w-3 sm:h-4 sm:w-4" /> : getStatusIcon(task.status)}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                      <h3 className="font-medium text-sm sm:text-base">{task.title}</h3>
                      {task.lockReason && (
                        <span className="text-xs text-amber-600 font-medium">({task.lockReason})</span>
                      )}
                      {task.isOptional && (
                        <Badge variant="outline" className="text-xs">Optional</Badge>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">{task.description}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-3 ml-9 sm:ml-0">
                  <div className="text-left sm:text-right">
                    {(task.status !== 'completed' && task.status !== 'opted-in') && (
                      <p className="text-xs sm:text-sm font-medium">
                        {task.lockReason === 'Coming Soon' ? 'Coming Soon' : `Due ${task.dueDate}`}
                      </p>
                    )}
                    <Badge variant={
                      task.locked
                        ? 'secondary'
                        : task.id === 'promotional-flyer'
                          ? (task.status === 'opted-in' ? 'default' : 'outline')
                          : (task.status === 'completed' ? 'default' : 'outline')
                    } className="mt-1 text-xs">
                      {task.locked ? 'locked' :
                       task.id === 'promotional-flyer' ?
                         (task.status === 'opted-in' ? 'Opted in' : 'Opted out') :
                         task.status}
                    </Badge>
                  </div>
                  {!task.locked && <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Modals */}
      <CompanyDetailsModal
        open={activeModal === 'company-details'}
        onClose={() => setActiveModal(null)}
      />
      <NetworkingPassesModal
        open={activeModal === 'networking-passes'}
        onClose={() => {
          setActiveModal(null)
          // Refresh networking passes status
          checkNetworkingPassesStatus()
        }}
        onOpenFOModal={() => {
          // Navigate to Benefits page with FO modal auto-open
          navigate('/company/benefits', { state: { openModal: 'olympiad' } })
        }}
      />
      <PresentationModal
        open={activeModal === 'presentation'}
        onClose={() => setActiveModal(null)}
      />
      <DemoGuidelinesModal
        open={activeModal === 'demo-guidelines'}
        onClose={() => {
          setActiveModal(null)
          // Refresh guidelines status
          checkGuidelinesStatus()
        }}
      />
      <PitchDeckFeedbackModal
        open={activeModal === 'presenter-info'}
        onClose={() => {
          setActiveModal(null)
          // Refresh pitch deck status
          checkPitchDeckStatus()
        }}
      />
      <PitchDeckSubmissionModal
        open={activeModal === 'pitch-submission'}
        onClose={() => {
          setActiveModal(null)
          // Refresh pitch deck status
          checkPitchDeckStatus()
        }}
      />
      <BrandPromotionModal
        open={activeModal === 'brand-promotion'}
        onClose={() => {
          setActiveModal(null)
          checkBrandPromotionStatus()
        }}
      />
      <TechnicalSetupModal
        open={activeModal === 'technical-setup'}
        onClose={() => {
          setActiveModal(null)
          // Refresh technical checklist status
          checkTechnicalChecklistStatus()
        }}
      />
      <PromotionalFlyerModal
        open={activeModal === 'promotional-flyer'}
        onClose={() => {
          setActiveModal(null)
          checkPromotionalFlyerStatus()
        }}
      />
      <CEOFeedbackModal
        open={activeModal === 'ceo-feedback'}
        onClose={() => setActiveModal(null)}
      />
    </div>
  )
}